/**
 * SHELL-1 · `授权 SURF-SHELL stay` — shared wayfinding (non-Response).
 * Location bar · typed handoff / return_to · notification deep-links · leave-seat confirm.
 * Soft≠Live: no authority math; Response UI not imported.
 */
import _React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, SPRING, SOFT, go, parseHashRoute } from "./ui.js";
import { labelOfPrimary, primaryOf } from "./route-stage-fence.js";
import { CAP_HOME } from "./cmc-entry-registry.js";
import { isDirtySeat, setDirtySeat } from "./quality-runtime.js";

/** Activate v3 phase → locbar seat (hash stays #/activate). */
let _activatePhaseSeat = "Find";
let _activatePhasePurpose = "Find SN/QR · claim custody · hand off to Machine";
const _activatePhaseListeners = new Set();

export function setActivatePhaseSeat(seat, purpose) {
  _activatePhaseSeat = String(seat || "Find").trim() || "Find";
  if (purpose != null) {
    _activatePhasePurpose = String(purpose || "").trim()
      || "Find SN/QR · claim custody · hand off to Machine";
  }
  _activatePhaseListeners.forEach((fn) => {
    try { fn({ seat: _activatePhaseSeat, purpose: _activatePhasePurpose }); }
    catch (_) { /* ignore */ }
  });
}

/** Safe return_to prefixes — unknown tokens fail closed (drop, do not invent). */
export const RETURN_SAFE_PREFIXES = Object.freeze([
  "/ops",
  "/machine",
  "/activate",
  "/automations",
  "/settings",
  "/ota",
  "/sa",
  "/fleet",
  "/edge",
  "/control",
  "/response",
]);

export function safeReturnToken(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s.startsWith("/")) return "";
  const pathOnly = parseHashRoute(s).pathname || s;
  if (pathOnly.includes("//") || pathOnly.includes("\\")) return "";
  for (const p of RETURN_SAFE_PREFIXES) {
    if (pathOnly === p || pathOnly.startsWith(p + "/")) return pathOnly;
  }
  return "";
}

export function appendQuery(path, params) {
  /* Tolerate "#/…" or "/…" — pathname never treats query as a subroute. */
  let base = String(path || "").trim() || "/ops";
  if (base.startsWith("#")) base = base.slice(1);
  if (!base.startsWith("/")) base = "/" + base;
  const qAt = base.indexOf("?");
  const pathPart = qAt >= 0 ? base.slice(0, qAt) : base;
  const sp = new URLSearchParams(qAt >= 0 ? base.slice(qAt + 1) : "");
  const bag = params && typeof params === "object" ? params : {};
  for (const [k, v] of Object.entries(bag)) {
    const val = String(v == null ? "" : v).trim();
    if (!val) continue;
    if (k === "return_to") {
      const tok = safeReturnToken(val);
      if (tok) sp.set("return_to", tok);
      continue;
    }
    sp.set(k, val);
  }
  const qs = sp.toString();
  return qs ? pathPart + "?" + qs : pathPart;
}

/** Typed handoff href — known keys only; unknown never become ids. */
export function buildHandoffHref(targetPath, opts = {}) {
  const o = opts && typeof opts === "object" ? opts : {};
  const bag = {};
  for (const k of ["device", "incident", "type", "view", "owner", "return_to"]) {
    if (o[k] != null && String(o[k]).trim()) bag[k] = String(o[k]).trim();
  }
  return appendQuery(targetPath, bag);
}

const SEAT_LABEL = Object.freeze({
  ops: {
    map: "Map",
    overview: "Now",
    ai: "Watch",
    workbench: "Run",
    zones: "Areas",
    areas: "Areas",
    dispatch: "Dispatch",
    devices: "Boards",
    alarms: "Alarms",
    cameras: "Cameras",
    resources: "Resources",
  },
  cameras: { live: "Live", retained: "Kept pictures", manage: "Register & bind" },
  response: { board: "Board", cases: "Needs You", robots: "Robots", drill: "Practice" },
  dispatch: { people: "People", robots: "Robots", drones: "Drones" },
  places: { zones: "Zones", sites: "Sites", groups: "Groups" },
  prepare: { readiness: "Readiness", drill: "Drills", playbook: "Site SOP" },
  skills: { library: "Library", assign: "Targets", waves: "Rollouts" },
  settings: {
    profile: "Profile",
    security: "Password",
    sessions: "Sign-ins",
    delegations: "Who can act",
    notify: "Alerts",
    alarm: "Alarm behaviour",
    trigger: "Capture start",
    ai: "Watch team",
    integrate: "Links",
    audit: "Export",
    billing: "Plan",
    danger: "Danger zone",
  },
  automations: { library: "Library", runs: "Runs", safety: "Safety", exceptions: "Exceptions" },
  ota: { base: "Base image" },
  sa: {
    caps: "Permissions",
    team: "Staff",
    models: "Models",
    "ai-tools": "Skills",
    tenant: "Tenants",
    custody: "Custody",
    factory: "Factory",
    "edge-lab": "Edge Lab",
    audit: "Audit",
    vault: "Backup export",
  },
  fleet: { firmware: "Firmware", device: "Board", group: "Group", zones: "Areas" },
  edge: { estate: "Estate", link: "Links", bench: "Workbench", tunnel: "Live path" },
  control: { shares: "Shares" },
});

const PURPOSE_BY_PRIMARY = Object.freeze({
  ops: "Watch the site. Continue or Hold — you stay on the loop.",
  cameras: "Live means pixels are playing. A ticket is not live.",
  response: "Needs You is the exception — not every alarm.",
  dispatch: "People and vehicles that can actually go.",
  places: "This site’s map and zones.",
  prepare: "Rehearse now so tonight is ready.",
  machine: "Claimed boards only. Empty stays empty.",
  activate: "Find SN/QR · claim custody · hand off to Machine",
  automations: "Write a rule, then prove it ran.",
  ota: "Board image campaign. Skills live on their own desk.",
  settings: "This seat, alerts, and the Watch team.",
  sa: "Platform controls for this estate.",
  fleet: "Fleet lab — not the nightly watch.",
  edge: "Edge lab — links and tunnels.",
  control: "Shares for this estate.",
  skills: "What a board may carry. Roll out, then watch the receipt.",
});

export function seatLabelOf(route) {
  const r = String(route || "");
  const seg = r.split("/").filter(Boolean);
  if (!seg.length) return "Home";
  const primary = seg[0];
  if (primary === "machine") {
    if (seg[1] === "groups") return "Groups";
    if (seg[1] === "capabilities" || seg[1] === "deployments") return "Capabilities";
    if (seg[1]) return "Detail";
    return "List";
  }
  if (primary === "activate") return _activatePhaseSeat || seg[1] || "Find";
  const tab = seg[1] || "";
  const map = SEAT_LABEL[primary];
  if (map) {
    if (tab && map[tab]) return map[tab];
    if (!tab) {
      const first = Object.values(map)[0];
      if (first) return first;
    }
  }
  if (primary === "ota") return "Campaign";
  return tab || "Desk";
}

export function purposeOf(route) {
  const primary = String(route || "").split("/").filter(Boolean)[0] || "ops";
  if (primary === "activate") return _activatePhasePurpose;
  return PURPOSE_BY_PRIMARY[primary] || "Where you are on this site.";
}

export function deskSayOf(route) {
  const parts = locationPartsOf(route);
  return [parts.primary, parts.seat].filter(Boolean).join(" · ");
}

export function objectLabelOf(route) {
  const seg = String(route || "").split("/").filter(Boolean);
  if (seg[0] === "machine" && seg[1] && seg[1] !== "groups" && seg[1] !== "capabilities" && seg[1] !== "deployments") {
    try { return decodeURIComponent(seg[1]); } catch (_) { return seg[1]; }
  }
  if (seg[0] === "machine" && seg[1] === "groups" && seg[2]) {
    try { return decodeURIComponent(seg[2]); } catch (_) { return seg[2]; }
  }
  if (seg[0] === "fleet" && (seg[1] === "device" || seg[1] === "group") && seg[2]) {
    try { return decodeURIComponent(seg[2]); } catch (_) { return seg[2]; }
  }
  return "";
}

export function locationPartsOf(route) {
  const primaryId = primaryOf(route);
  const seg = String(route || "").split("/").filter(Boolean);
  const onActivate = seg[0] === "activate";
  const purpose = purposeOf(route);
  const seat = onActivate
    ? (_activatePhaseSeat || seatLabelOf(route))
    : seatLabelOf(route);
  return {
    primaryId,
    primary: labelOfPrimary(primaryId),
    seat,
    object: objectLabelOf(route),
    purpose,
  };
}

/**
 * Notification → owning object. Fail closed to Ops Alarms (not a silent invent).
 */
export function notifDeepLinkOf(ev, returnTo) {
  const e = ev && typeof ev === "object" ? ev : {};
  const device = String(e.device || e.device_id || e.dev || "").trim();
  const incident = String(e.incident || e.incident_id || "").trim();
  const typ = String(e.event_type || e.type || e.txt || "").toLowerCase();
  const ret = safeReturnToken(returnTo || "/ops");
  if (incident) {
    return buildHandoffHref(CAP_HOME.needs_you.path, { incident, return_to: ret });
  }
  if (device) {
    return buildHandoffHref("/machine/" + encodeURIComponent(device), { return_to: ret });
  }
  if (/alarm|siren|alert/.test(typ)) {
    return buildHandoffHref("/ops/alarms", { return_to: ret });
  }
  return buildHandoffHref("/ops/alarms", { return_to: ret });
}

function LeaveSeatDrawer({ fromLabel, toLabel, destSay, objectLabel, onYes, onNo }) {
  return html`
    <${AnimatePresence}>
      <${M.div}
        key="leave-seat"
        className="scrim cmc-drawer-scrim"
        role="presentation"
        initial=${{ opacity: 0 }}
        animate=${{ opacity: 1 }}
        exit=${{ opacity: 0 }}
        transition=${SOFT}
        onClick=${onNo}>
        <${M.div}
          className="sheetlet cmc-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shell-leave-title"
          data-cmc-shell="leave-seat"
          initial=${{ opacity: 0, y: 18 }}
          animate=${{ opacity: 1, y: 0 }}
          transition=${SPRING}
          onClick=${(e) => e.stopPropagation()}>
          <div className="cmc-drawer__in">
            <header className="cmc-drawer__hd">
              <div className="cmc-drawer__hd-copy">
                <span className="cmc-drawer__k">Leave this desk</span>
                <h3 className="cmc-drawer__title" id="shell-leave-title">
                  ${fromLabel} → ${toLabel}
                </h3>
              </div>
              <button type="button" className="cmc-drawer__x" onClick=${onNo} aria-label="Stay here">
                <${Ico} d="close" w=${14}/>
              </button>
            </header>
            <p className="cmc-drawer__lede">
              You will open another desk. Unsaved work on this one is left behind.
            </p>
            <section className="cmc-drawer__sec">
              <div className="cmc-drawer__row"><span>Opening</span><b>${destSay || toLabel}</b></div>
              ${objectLabel
                ? html`<div className="cmc-drawer__row"><span>About</span><b>${objectLabel}</b></div>`
                : null}
            </section>
            <footer className="cmc-drawer__ft">
              <button type="button" className="btn btn--ghost" onClick=${onNo}>Stay here</button>
              <button type="button" className="btn btn--go" autofocus onClick=${onYes}
                data-cmc-shell-confirm="leave">Open it</button>
            </footer>
          </div>
        <//>
      <//>
    <//>`;
}

/**
 * Cross-primary navigation with explicit leave-seat confirm + return_to.
 * Same-primary goes immediately unless QUALITY-1 dirty seat is set.
 */
export function confirmLeaveSeat({ fromRoute, targetPath, objectLabel }) {
  const fromId = primaryOf(fromRoute);
  const toPath = String(targetPath || "").trim();
  const toId = primaryOf(parseHashRoute(toPath.startsWith("/") ? toPath : "/" + toPath).pathname || toPath);
  if (!toPath) return Promise.resolve(false);
  const dirty = !!isDirtySeat();
  if (fromId === toId && !dirty) {
    go(buildHandoffHref(toPath, { return_to: safeReturnToken(fromRoute) || undefined }));
    return Promise.resolve(true);
  }
  const href = buildHandoffHref(toPath, {
    return_to: safeReturnToken(fromRoute) || undefined,
  });
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("data-cmc-shell-host", "leave-seat");
    document.body.appendChild(host);
    const root = createRoot(host);
    const finish = (ok) => {
      try { root.unmount(); } catch (_) { /* ignore */ }
      try { host.remove(); } catch (_) { /* ignore */ }
      if (ok) {
        try { setDirtySeat(false); } catch (_) { /* ignore */ }
        go(href);
      }
      resolve(!!ok);
    };
    root.render(html`<${LeaveSeatDrawer}
      fromLabel=${deskSayOf(fromRoute)}
      toLabel=${deskSayOf(toPath)}
      destSay=${deskSayOf(toPath)}
      objectLabel=${dirty ? (objectLabel || "Unsaved draft") : (objectLabel || "")}
      onYes=${() => finish(true)}
      onNo=${() => finish(false)}
    />`);
  });
}

/** Universal location bar — excluded on Map root and frozen Response. */
export function LocationBar({ route, siteLabel }) {
  const onActivate = String(route || "").split("/").filter(Boolean)[0] === "activate";
  const [phaseTick, setPhaseTick] = useState(0);
  useEffect(() => {
    if (!onActivate) return undefined;
    const fn = () => setPhaseTick((n) => n + 1);
    _activatePhaseListeners.add(fn);
    return () => { _activatePhaseListeners.delete(fn); };
  }, [onActivate]);
  const parts = locationPartsOf(route);
  const trail = [parts.primary, parts.seat, parts.object].filter(Boolean).join(" / ");
  return html`
    <${M.div} className="cmc-locbar" data-cmc-shell="shell-1" role="navigation"
      aria-label="Location" key=${trail + String(phaseTick) || "loc"}
      initial=${{ opacity: 0, y: -6 }} animate=${{ opacity: 1, y: 0 }} transition=${SOFT}>
      <div className="cmc-locbar__trail">
        <span className="cmc-locbar__crumb">${trail}</span>
        <span className="cmc-locbar__purpose">${parts.purpose}</span>
      </div>
      <div className="cmc-locbar__site" title="This site — organisation Sites; areas stay on the map">
        <u>This site</u><b>${siteLabel || "—"}</b>
      </div>
    <//>`;
}
